/** A standard laminate sheet is 8 ft x 4 ft. */
export const SHEET_SQFT = 32
const DEFAULT_WASTAGE_PCT = 10
const ok = (...ns: number[]) => ns.every((n) => Number.isFinite(n) && n > 0)

/** Sheets needed to cover a shutter front (or both faces) with 10% wastage, rounded up to whole sheets. */
export function laminateSheets(i: { widthFt: number; heightFt: number; sides: 1 | 2; wastagePct?: number }): { areaSqft: number; sheets: number } | null {
  const wastage = i.wastagePct ?? DEFAULT_WASTAGE_PCT
  if (!ok(i.widthFt, i.heightFt) || (i.sides !== 1 && i.sides !== 2) || !Number.isFinite(wastage) || wastage < 0) return null
  const areaSqft = round1(i.widthFt * i.heightFt * i.sides)
  // Integer arithmetic in tenths of a sq ft and whole percent, so 1600 x 1.1 does not round up to an extra sheet.
  const tenths = Math.round(areaSqft * 10)
  return { areaSqft, sheets: Math.ceil((tenths * (100 + wastage)) / (10 * 100 * SHEET_SQFT)) }
}

/**
 * Louver or wall panels for a wall, columns across by panel width, plus 10% wastage.
 * `panels` assumes offcuts are joined: the running length (columns x wall height) is cut from whole panels.
 * `panelsUncut` buys whole panels for every row of every column, for runs that must be one uncut panel.
 */
export function sqftToPanels(i: { wallWidthFt: number; wallHeightFt: number; panelWidthIn?: number; panelLengthFt?: number; wastagePct?: number }): { areaSqft: number; columns: number; rows: number; panels: number; panelsUncut: number } | null {
  const panelWidthIn = i.panelWidthIn ?? 6, panelLengthFt = i.panelLengthFt ?? 8, wastage = i.wastagePct ?? DEFAULT_WASTAGE_PCT
  if (!ok(i.wallWidthFt, i.wallHeightFt, panelWidthIn, panelLengthFt) || !Number.isFinite(wastage) || wastage < 0) return null
  const columns = Math.ceil((i.wallWidthFt * 12) / panelWidthIn)
  const rows = Math.ceil(i.wallHeightFt / panelLengthFt)
  // Integer arithmetic in tenths of a foot and whole percent, so 50 x 1.1 (55.00000000000001 in floats) does not
  // round up to an extra panel.
  const joined = Math.ceil((columns * Math.round(i.wallHeightFt * 10)) / Math.round(panelLengthFt * 10))
  return {
    areaSqft: round1(i.wallWidthFt * i.wallHeightFt),
    columns,
    rows,
    panels: Math.ceil((joined * (100 + wastage)) / 100),
    panelsUncut: Math.ceil((columns * rows * (100 + wastage)) / 100),
  }
}

const round1 = (n: number) => Math.round(n * 10) / 10
